import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox, Input } from "@progress/kendo-react-inputs";

// Mirrors the friendly descriptions ClientOEStatuses.GetDesc produces on the
// server -- the values Status and Group Status actually carry.
export const STATUS_FILTER_OPTIONS = [
  "Not Started",
  "In Progress",
  "Submitted for Review",
  "Published",
  "Published Default",
  "Publish In Progress",
  "Publish Error",
];

/** True when this descriptor, or anything nested under it, filters on `field`. */
const targetsField = (descriptor, field) =>
  descriptor.field === field ||
  (descriptor.filters || []).some((child) => targetsField(child, field));

/**
 * Every value ticked for one column, in the order they were ticked.
 *
 * Each column's selection is its own composite descriptor nested inside the
 * grid's -- { and: [ { or: [eq, eq] }, ... ] } -- so this walks the tree rather
 * than reading only the top level. Blank values are dropped: an empty string
 * would otherwise reach the server as a status nothing matches.
 */
export function getFilterValues(filter, field) {
  const descriptors = filter?.filters;
  if (!descriptors) {
    return [];
  }

  return descriptors.flatMap((descriptor) => {
    if (descriptor.filters) {
      return getFilterValues(descriptor, field);
    }
    return descriptor.field === field && descriptor.value ? [descriptor.value] : [];
  });
}

/**
 * The grid filter with one column's statuses set to `values`, leaving every
 * other column's selection as it was.
 *
 * The statuses are grouped under `or` so any of them matches, and the group
 * replaces whatever this column held before rather than stacking a second one.
 * Returns null when nothing is filtered any more -- that is how Kendo and the
 * grid read "cleared".
 */
export function buildStatusFilter(currentFilter, field, values) {
  const otherColumns = (currentFilter?.filters || []).filter(
    (descriptor) => !targetsField(descriptor, field),
  );

  const thisColumn =
    values.length > 0
      ? [
          {
            logic: "or",
            filters: values.map((value) => ({ field, operator: "eq", value })),
          },
        ]
      : [];

  const filters = [...otherColumns, ...thisColumn];

  return filters.length > 0 ? { logic: "and", filters } : null;
}

/**
 * Normalizes a raw list of names into checkbox-menu options: drops blanks and
 * non-strings, de-duplicates, and sorts.
 *
 * The option lists come from admin-managed API lists where a row can carry a
 * null name, and two rows can carry the same one. An undefined option would
 * throw on the menu's search `toLowerCase`, and a repeat would collide on the
 * React key, so both are cleared out here rather than at every call site.
 */
export function toCheckboxOptions(values) {
  const cleaned = (values || []).filter(
    (value) => typeof value === "string" && value.trim() !== "",
  );
  return [...new Set(cleaned)].sort();
}

/**
 * Builds a header-menu component for a fixed-option, checkbox-per-value filter --
 * a checkbox per option, so more than one can be filtered on at a time.
 *
 * The options are a fixed list rather than the values on screen -- these grids are
 * server-paged, so deriving them from the current page would offer only the
 * handful of values that page happens to contain.
 *
 * Written out rather than delegated to Kendo's GridColumnMenuCheckboxFilter,
 * which hard-codes a "Filter" heading and a select-all row above the list, with
 * no prop to turn either off. `searchable` opts a column into a search box that
 * narrows the visible checkboxes (selections outside the current search stay
 * ticked); pass it for longer lists like Broker or Sales rep, and leave it off
 * for short fixed lists like Status.
 */
export function createCheckboxColumnMenu(options, { searchable = false } = {}) {
  return function CheckboxColumnMenu({ column, filter, onFilterChange, onCloseMenu }) {
    const field = column.field;
    const [ticked, setTicked] = React.useState(() => getFilterValues(filter, field));
    const [searchTerm, setSearchTerm] = React.useState("");

    const visibleOptions = searchable
      ? options.filter((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : options;

    const toggle = (value, checked) =>
      setTicked((current) =>
        checked ? [...current, value] : current.filter((v) => v !== value),
      );

    const publish = (values, event) => {
      onFilterChange(buildStatusFilter(filter, field, values), event);
      if (onCloseMenu) {
        onCloseMenu();
      }
    };

    return (
      <div className="k-filter-menu k-group k-reset">
        <div className="k-filter-menu-container">
          {searchable && (
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginBottom: "8px", width: "100%" }}
            />
          )}
          <ul className="k-reset k-multicheck-wrap">
            {visibleOptions.map((value) => (
              <li className="k-item" key={value}>
                <Checkbox
                  label={value}
                  checked={ticked.includes(value)}
                  onChange={(e) => toggle(value, e.value)}
                />
              </li>
            ))}
          </ul>
          <div style={{ fontSize: "12px", color: "#666", padding: "4px 0" }}>
            {ticked.length} selected of {options.length}
          </div>
          <div className="k-actions k-hstack k-justify-content-stretch">
            <Button
              onClick={(e) => {
                setTicked([]);
                publish([], e);
              }}
            >
              Clear
            </Button>
            <Button themeColor="primary" onClick={(e) => publish(ticked, e)}>
              Filter
            </Button>
          </div>
        </div>
      </div>
    );
  };
}

/** The Status and Group Status header menus. See createCheckboxColumnMenu. */
export const StatusColumnMenu = createCheckboxColumnMenu(STATUS_FILTER_OPTIONS);
